import { FC, useCallback, useState } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { useTheme } from '@mui/material/styles';
import graphql from 'babel-plugin-relay/macro';

import UploadFileIcon from '@mui/icons-material/UploadFile';
import ReactJson from 'react-json-view';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DropZone from 'components/DropZone';

import { useErrorHandler, useHoverable } from 'lib';
import { GoogleCredentials_data$key } from '__generated__/GoogleCredentials_data.graphql';
import { GoogleCredentialsMutation } from '__generated__/GoogleCredentialsMutation.graphql';
import CircularProgress from '@mui/material/CircularProgress';

type Props = {
  data: GoogleCredentials_data$key;
};

const GoogleCredentials: FC<Props> = props => {
  const theme = useTheme();
  const errorHandler = useErrorHandler();
  const { hovering, ...hoverable } = useHoverable();
  const [changing, setChanging] = useState(false);

  const { googleCredentials } = useFragment(
    graphql`
      fragment GoogleCredentials_data on Query {
        googleCredentials
      }
    `,
    props.data
  );

  const [upload, uploading] = useMutation<GoogleCredentialsMutation>(graphql`
    mutation GoogleCredentialsMutation($file: Upload!) {
      uploadGoogleCredentials(file: $file)
    }
  `);

  const handleChangeClick = useCallback(() => {
    setChanging(true);
  }, []);

  const handleCancelClick = useCallback(() => {
    setChanging(false);
  }, []);

  const handleDrop = useCallback(
    (accepted: File[]) => {
      accepted.forEach(file => {
        upload({
          uploadables: { file },

          variables: {
            file: null,
          },

          ...errorHandler({
            onCompleted: () => {
              setChanging(false);
            },
          }),

          updater: (store, { uploadGoogleCredentials }) => {
            store
              .getRoot()
              .setValue(uploadGoogleCredentials, 'googleCredentials');
          },
        });
      });
    },
    [errorHandler, upload]
  );

  return (
    <Box {...hoverable} sx={{ position: 'relative' }}>
      {!googleCredentials || changing ? (
        <>
          {changing && (
            <Button
              variant="outlined"
              sx={{ position: 'absolute', top: 5, right: 5, zIndex: 50 }}
              onClick={handleCancelClick}>
              Atšaukti
            </Button>
          )}

          <DropZone
            multiple={false}
            onDrop={handleDrop}
            icon={
              uploading ? (
                <CircularProgress color="secondary" size={50} />
              ) : (
                <UploadFileIcon fontSize="large" sx={{ height: '50px' }} />
              )
            }
            accept=".json"
          />
        </>
      ) : (
        <>
          {hovering && (
            <Button
              variant="outlined"
              sx={{ position: 'absolute', top: 0, right: 0, zIndex: 50 }}
              onClick={handleChangeClick}>
              Keisti
            </Button>
          )}

          <ReactJson
            name={false}
            displayDataTypes={false}
            displayObjectSize={false}
            collapseStringsAfterLength={50}
            theme={theme.palette.mode === 'dark' ? 'twilight' : 'rjv-default'}
            src={JSON.parse(googleCredentials)}
          />
        </>
      )}
    </Box>
  );
};

export default GoogleCredentials;
